import 'vuetify/styles'
import { createVuetify } from 'vuetify'
import { createVueI18nAdapter } from 'vuetify/locale/adapters/vue-i18n'
import { useI18n } from 'vue-i18n'
import { aliases as mdiAliases, mdi } from 'vuetify/iconsets/mdi-svg'
import i18n from './i18n'
import {
  mdiArrowExpand,
  mdiArrowLeft,
  mdiBellOutline,
  mdiChartBar,
  mdiChatQuestion,
  mdiAccountCircle,
  mdiAccountGroup,
  mdiAccountKey,
  mdiAccountMinus,
  mdiAccountPlus,
  mdiAccountLock,
  mdiAccountRemove,
  mdiAccountSearch,
  mdiAccountSupervisor,
  mdiCheckBold,
  mdiCloseThick,
  mdiViewDashboard,
  mdiCheck,
  mdiClose,
  mdiDownload,
  mdiDelete,
  mdiDevices,
  mdiCamera,
  mdiChevronDown,
  mdiChevronUp,
  mdiCircleOutline,
  mdiCircleSlice2,
  mdiCircleSlice4,
  mdiCircleSlice6,
  mdiCircleSlice8,
  mdiFileDocument,
  mdiHelpCircleOutline,
  mdiQrcodeScan,
  mdiRefresh,
  mdiCompassOutline,
  mdiOfficeBuildingMarkerOutline,
  mdiCalendarMonthOutline,
  mdiThermometerLines,
  mdiFilePdfBox,
  mdiFileCad,
  mdiFileImage,
  mdiFileTableBox,
  mdiFileWordBox,
  mdiFilterVariant,
  mdiFormatListBulleted,
  mdiCloudDownload,
  mdiEmail,
  mdiEyeOff,
  mdiHome,
  mdiKey,
  mdiLockReset,
  mdiDomain,
  mdiLock,
  mdiLockCheckOutline,
  mdiLockAlert,
  mdiLockOpenVariant,
  mdiExitToApp,
  mdiMap,
  mdiFormTextboxPassword,
  mdiPhone,
  mdiPencilOutline,
  mdiMagnify,
  mdiEmailArrowRight,
  mdiEye,
  mdiTrashCanOutline,
  mdiWidgets,
  mdiToolbox,
  mdiEyeOutline,
  mdiFlash,
  mdiHammerWrench,
  mdiCashMultiple,
  mdiReply,
  mdiCommentTextMultipleOutline,
  mdiPlus,
  mdiTicketAccount,
  mdiFileUpload,
  mdiSend,
  mdiArrowCollapse,
  mdiMicrophone,
  mdiZipBox,
} from '@mdi/js'

// App-specific icon aliases, referenced in templates as `$account`, `$delete`, etc.
const customAliases = {
  account: mdiAccountCircle,
  accountLock: mdiAccountLock,
  accountRemove: mdiAccountRemove,
  accountSearch: mdiAccountSearch,
  accountSupervisor: mdiAccountSupervisor,
  addContractor: mdiAccountPlus,
  addNew: mdiPlus,
  askBowvue: mdiChatQuestion,
  back: mdiArrowLeft,
  barChart: mdiChartBar,
  bell: mdiBellOutline,
  expandFullscreen: mdiArrowExpand,
  comment: mdiCommentTextMultipleOutline,
  closeSupportTicket: mdiCheckBold,
  dashboard: mdiViewDashboard,
  delete: mdiTrashCanOutline,
  devices: mdiDevices,
  // Generic forms of the two below, for anything that is not a location. The
  // location-specific names predate them and are still used by the locations page;
  // both spellings resolve to the same icon.
  cancel: mdiClose,
  check: mdiCheck,
  disableLocation: mdiClose,
  enableLocation: mdiCheck,
  document: mdiFileDocument,
  help: mdiHelpCircleOutline,
  qrScan: mdiQrcodeScan,
  compass: mdiCompassOutline,
  facility: mdiOfficeBuildingMarkerOutline,
  calendar: mdiCalendarMonthOutline,
  thermal: mdiThermometerLines,
  filePdf: mdiFilePdfBox,
  fileCad: mdiFileCad,
  fileImage: mdiFileImage,
  fileSheet: mdiFileTableBox,
  fileWord: mdiFileWordBox,
  fileZip: mdiZipBox,
  download: mdiCloudDownload,
  deleteDocument: mdiDelete,
  email: mdiEmail,
  expand: mdiChevronDown,
  edit: mdiPencilOutline,
  filter: mdiFilterVariant,
  list: mdiFormatListBulleted,
  maximize: mdiArrowExpand,
  refresh: mdiRefresh,
  restore: mdiArrowCollapse,
  microphone: mdiMicrophone,
  reduce: mdiChevronUp,
  grantAdmin: mdiAccountKey,
  arcFlash: mdiFlash,
  hide: mdiEyeOff,
  home: mdiHome,
  key: mdiKey,
  location: mdiDomain,
  lock: mdiLock,
  loto: mdiLockCheckOutline,
  lockOpen: mdiLockOpenVariant,
  logout: mdiExitToApp,
  map: mdiMap,
  openSupportTicket: mdiCloseThick,
  password: mdiFormTextboxPassword,
  pdfDownload: mdiDownload,
  phone: mdiPhone,
  photo: mdiCamera,
  preventativeMaintenance: mdiToolbox,
  passwordReset: mdiLockAlert,
  removeAdmin: mdiAccountMinus,
  // Take a staffer off the scheduler's board (not a role change, see
  // removeAdmin, which shares the glyph).
  removeFromSchedule: mdiAccountMinus,
  reply: mdiReply,
  resetLocation: mdiLockReset,
  search: mdiMagnify,
  send: mdiSend,
  sendEmail: mdiEmailArrowRight,
  show: mdiEye,
  statusZero: mdiCircleOutline,
  statusQuarter: mdiCircleSlice2,
  statusHalf: mdiCircleSlice4,
  statusThreeQuarter: mdiCircleSlice6,
  statusFull: mdiCircleSlice8,
  tools: mdiHammerWrench,
  users: mdiAccountGroup,
  visMechInspection: mdiEyeOutline,
  widgets: mdiWidgets,
  financial: mdiCashMultiple,
  ticket: mdiTicketAccount,
  upload: mdiFileUpload,
}

export default createVuetify({
  // Drive Vuetify's own component strings (data-table footers, date pickers,
  // pagination, etc.) from the app's vue-i18n instance, so they switch language
  // with everything else. The `$vuetify`-keyed messages live in plugins/i18n.js.
  locale: {
    adapter: createVueI18nAdapter({ i18n, useI18n }),
  },
  icons: {
    defaultSet: 'mdi',
    aliases: { ...mdiAliases, ...customAliases },
    sets: { mdi },
  },
  theme: {
    // The startup theme. plugins/theme.js overrides this immediately from the
    // stored preference (or the OS, when that preference is 'system'), so this
    // value only decides the first paint before that runs.
    defaultTheme: 'light',
    themes: {
      // Every token below is a LIGHTENED counterpart of its light-mode value,
      // not the same hex. Measured against the #1E1E1E surface, the light
      // palette does not survive the move: brand #BF0000 lands at 2.55:1,
      // primary #0074d9 at 3.57:1 and studyCurrent #1b7a2b at 3.07:1, all
      // below AA for body text. The values here clear 4.5:1.
      dark: {
        dark: true,
        colors: {
          background: '#121212',
          surface: '#1E1E1E',
          primary: '#4DA3FF', // 6.35:1
          // Still unmistakably the Bowtie red, just light enough to read.
          brand: '#FF5252', // 5.22:1
          // The app bar's fill, deliberately the SAME deep red in both themes.
          // `brand` is lightened for dark mode because it has to work as text
          // and icons; using that lightened red across a whole app bar would
          // glare. A branded header staying branded in the dark is the intent,
          // not an oversight.
          brandSurface: '#BF0000',
          // The assistant's chat bubble. A step above the #1E1E1E panel it sits
          // on, the way a raised surface reads in the dark, rather than the
          // white sheet it used to be, which glared on the panel. White ink on it
          // is 14.35:1; the panel itself is the border's job to separate.
          chatBubble: '#2A2A2A',
          'on-chatBubble': '#FFFFFF',
          secondary: '#FF6B6B',
          accent: '#8AB4F8',
          error: '#FF6B6B', // 6.01:1
          info: '#4DD0E1',
          success: '#2ECC40', // 7.80:1, unchanged
          // The text-safe pair, see the light palette for why they exist. On
          // #1E1E1E these two hues already clear AA as text, so here they are
          // simply `success` and `error` again. The tokens still earn their
          // keep: a component asks for the text-safe shade and gets the right
          // one in both themes without knowing which theme it is in.
          successText: '#2ECC40', // 7.80:1
          errorText: '#FF6B6B', // 6.01:1
          // Same value as `warning` above: on a dark surface the amber is
          // already legible as ink (6.85:1), so this token exists for symmetry
          // with the light theme rather than to change anything.
          warningText: '#FF851B', // 6.85:1
          // Drawn as a SOLID block, so the bar is judged against the empty cell
          // behind it rather than as text: #43A047 sits at 5.05:1 on the dark
          // cell and takes black text at 6.36:1. The light-mode pastel would
          // glare at that size on a dark board.
          scheduleConfirmed: '#43A047',
          // Tonal text on its own tint, the same role as in light mode.
          studyCurrent: '#66BB6A', // 7.05:1
          warning: '#FF851B', // 6.85:1
          // Text drawn ON each fill above. Vuetify guesses this when it is not
          // given, and it guessed white: white on #FF5252 is 3.19:1, which is
          // why every brand button read as washed out. These lightened fills
          // all want BLACK text, measured below. The one exception is
          // brandSurface, which stays the deep #BF0000 and keeps white.
          //
          // The tension is real and worth stating: a red pale enough to be
          // READ as text on #1E1E1E is too pale to HOST white text. Splitting
          // the two roles (brand for text, brandSurface for fills) is what lets
          // both clear AA.
          'on-primary': '#000000', // 8.00:1
          'on-brand': '#000000', // 6.58:1
          'on-secondary': '#000000', // 7.57:1
          'on-error': '#000000', // 7.57:1
          'on-info': '#000000', // 11.43:1
          'on-success': '#000000', // 9.83:1
          'on-warning': '#000000', // 8.62:1
          'on-scheduleConfirmed': '#000000', // 6.36:1
          'on-studyCurrent': '#000000', // 8.88:1
          'on-brandSurface': '#FFFFFF', // 6.53:1
        },
      },
      light: {
        dark: false,
        colors: {
          primary: '#0074d9',
          // Bowtie brand red.
          brand: '#BF0000',
          // The app bar's fill. Identical to `brand` here; it earns its own
          // token because the DARK theme lightens `brand` for legibility and
          // the app bar must not follow it. See the dark palette above.
          brandSurface: '#BF0000',
          // The assistant's chat bubble: white on the white panel, set off by its
          // border, exactly as it looked before it became a token. The dark
          // palette is where it differs.
          chatBubble: '#FFFFFF',
          'on-chatBubble': '#000000',
          // Pinned like the dark one, so Vuetify does not decide the ink: the
          // chat bubble and the avatar draw white on this fill.
          'on-brandSurface': '#FFFFFF', // 6.53:1
          secondary: '#ff4136',
          accent: '#001f3f',
          // ERROR IS A FILL, AND ITS INK IS WHITE.
          //
          // Both halves are pinned on purpose. Vuetify picks an `on-` colour by
          // APCA with a bias toward white, which disagrees with WCAG: on the old
          // #ff4136 it chose white and landed at 3.46:1, under the 4.5:1 AA
          // floor, on 23 solid error buttons and every error alert.
          //
          // The fix darkens the FILL rather than flipping the ink to black.
          // Black on #ff4136 scores 6.06:1, but the WCAG formula over-rewards
          // black on saturated reds, and a bright red destructive button with
          // black text does not read as one. A darker red with white ink is both
          // the convention and a genuine pass.
          error: '#d32f2f', // 4.98:1 with white
          'on-error': '#FFFFFF',
          info: '#39CCCC',
          success: '#2ecc40',
          // A lighter green for the scheduler's CONFIRMED state, which is drawn
          // as a SOLID block (see chipVariant in schedule.vue). `success`
          // (#2ecc40) reads heavy at that size. Its own token rather than a
          // change to `success`, which a dozen other screens use.
          //
          // Lightening trades block-presence for softness: this sits at ~1.47:1
          // against the white cell behind it. Going much paler starts to lose
          // the block against an empty cell, which is the point of drawing
          // confirmed jobs solid in the first place.
          scheduleConfirmed: '#9ce7a5',
          // A darker green for the Study Renewals "Studied <date>" chip, which is
          // drawn TONAL. The colour is both the text and, at low alpha, the fill
          // behind it. `success` (#2ecc40) is sized for solid fills and white text;
          // as tonal text on its own tint it lands at 1.86:1, which is why that chip
          // was unreadable. This sits at 4.74:1 on the same fill (5.43:1 on white),
          // clearing AA for body text.
          //
          // Its own token rather than a change to `success`, for the same reason as
          // scheduleConfirmed above: a dozen other screens use that value.
          studyCurrent: '#1b7a2b',
          // WARNING IS A FILL, AND ITS INK IS BLACK.
          //
          // Unpinned, Vuetify's APCA pick put WHITE on this amber: 2.44:1, the
          // worst pair in either theme, on four solid buttons (disable device,
          // disable selected, reset condition of maintenance).
          //
          // Fixed by pinning the INK rather than darkening the fill, which is the
          // OPPOSITE of what `error` above needed and deliberately so. Black on a
          // saturated red reads as a caution stripe rather than a destructive
          // action, so error had to darken instead; black on amber is the hazard
          // convention and scores far better than any darkened orange could
          // (amber is inherently light, so white never clears AA on it without
          // turning the colour brown). The DARK theme already settled this exact
          // question for this exact hex.
          warning: '#ff851b',
          'on-warning': '#000000', // 8.62:1
          // `success` and `error` above are sized for SOLID fills. As TEXT, or
          // as the 1px border of an outlined button, they land at 2.14:1 and
          // 3.46:1 on this white surface: both under the 4.5:1 AA floor, and
          // the green is genuinely unreadable. These are the same two hues
          // darkened until they clear AA, for the outlined / text / tonal
          // variants. Reach for these whenever the colour IS the ink.
          //
          // successText is the same value as studyCurrent above, which is this
          // green under an older and narrower name (it was added for one chip).
          successText: '#1b7a2b', // 5.43:1
          errorText: '#c62828', // 5.62:1
          // Amber as INK on white is 2.44:1, the worst pair in the app, and it
          // is what 28 tonal chips, alerts and text buttons were drawing their
          // labels in. Darkened until it clears AA, and kept orange rather than
          // pushed toward red so it stays distinguishable from errorText.
          warningText: '#B35300', // 5.06:1
        },
      },
    },
  },
})
